import React, {
  useCallback,
  useState,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import InputField from 'components/InputField';
import DropzoneField from 'components/DropzoneField';
import TextAreaField from 'components/TextAreaField';
import StatusBadge from 'router/subrouters/Dash/subrouters/Business/pages/Businesses/components/StatusBadge';
import DetailPageField from 'router/subrouters/ListingCreationWizard/common-pages/ListingDetailsPreview/components/DetailPageField';
import {
  SPONSORED_ADVERT_CARD_TYPE,
  SponsoredAdvert,
} from 'models/SponsoredAdvert';
import api from 'api';
import { DropzoneDelete, DropzoneUpload } from 'models/Dropzone';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { Button, Loader } from 'ncoded-component-library';
import {
  getIsAdStatusDrafted,
  getIsAdStatusPaused,
  getIsAdTypeMedRec,
  STATUS_COPY_MAP,
} from '../../utils';
import { sentryError } from 'utils';
import confirm from 'modules/confirm';
import TrashIcon from 'icons/Trash.icon';
import PlayIcon from 'icons/Play.icon';
import PauseIcon from 'icons/Pause.icon';
import RadioSliderField from 'components/RadioSliderField';

import './EditSelectedAd.styles.scss';

type EditSelectedAdProps = {
  className?: string;
  selectedAd: SponsoredAdvert;
  addSponsoredAdvert: (
    operation: 'add' | 'update',
    ad: SponsoredAdvert,
  ) => void;
  updateSponsoredAdverts: (
    operation: 'delete' | 'toggle-pause',
    advert: SponsoredAdvert,
  ) => void;
};

const COVER_PHOTO_FILE_NAME = 'coverPhoto';
const LOGO_FILE_NAME = 'logoImage';

const EditSelectedAd: React.FC<EditSelectedAdProps> = (props) => {
  const { className, selectedAd, addSponsoredAdvert, updateSponsoredAdverts } =
    props;

  const [currentlyEditedAd, setCurrentlyEditedAd] = useState(selectedAd);
  const [isLoading, setIsLoading] = useState(false);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const isPaused = getIsAdStatusPaused(currentlyEditedAd?.status);

  const classes = classNames('edit-selected-ad', className);

  const cardOptions = useMemo(
    () => [
      {
        label: t('stripAd'),
        value: SPONSORED_ADVERT_CARD_TYPE.STRIP_AD,
      },
      {
        label: t('medRec'),
        value: SPONSORED_ADVERT_CARD_TYPE.MED_REC,
      },
      { label: t('both'), value: SPONSORED_ADVERT_CARD_TYPE.BOTH },
    ],
    [t],
  );

  const deleteAdvert = useCallback(async () => {
    if (
      await confirm(
        {
          title: t('deleteAdvert'),
          content: t('wantToDeleteAdvert'),
        },
        true,
      )
    ) {
      setIsLoading(true);
      try {
        await api.sponsoredListings.deleteSponsoredAdvert(selectedAd._id);
        popSuccess(t('deletedAdvert', { name: selectedAd.title }));
        updateSponsoredAdverts('delete', selectedAd);

        const { data } = await api.sponsoredListings.getLatestDraft();
        setCurrentlyEditedAd(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    }
  }, [selectedAd, popServerError, popSuccess, t, updateSponsoredAdverts]);

  const togglePauseAdvert = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await api.sponsoredListings[
        isPaused ? 'unpauseSponsoredAdvert' : 'pauseSponsoredAdvert'
      ](selectedAd._id);
      popSuccess(
        t('pausedAdvert', {
          name: selectedAd.title,
          action: t(isPaused ? 'resumedAd' : 'pausedAd'),
        }),
      );
      updateSponsoredAdverts('toggle-pause', data);
      setCurrentlyEditedAd(data);
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedAd,
    isPaused,
    popServerError,
    popSuccess,
    t,
    updateSponsoredAdverts,
  ]);

  useEffect(() => {
    if (selectedAd) {
      setCurrentlyEditedAd(selectedAd);
    } else {
      (async () => {
        try {
          setIsLoading(true);
          const { data } = await api.sponsoredListings.getLatestDraft();

          setCurrentlyEditedAd(data);
        } catch (e) {
          popServerError(e);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [popServerError, popSuccess, selectedAd, t]);

  const onSubmit = useCallback(
    async (val: SponsoredAdvert) => {
      setIsLoading(true);

      try {
        const { data } = await api.sponsoredListings.patchSponsoredAdvert(
          currentlyEditedAd._id,
          val,
        );

        if (!getIsAdStatusDrafted(data?.status)) {
          addSponsoredAdvert(selectedAd ? 'update' : 'add', data);
          setCurrentlyEditedAd(data);

          if (getIsAdStatusDrafted(val.status)) {
            popSuccess(t(STATUS_COPY_MAP.PUBLISHED_NEW_AD));
          } else {
            popSuccess(t(STATUS_COPY_MAP.UPDATED_EXISTING_AD));
          }
        } else {
          popSuccess(t(STATUS_COPY_MAP.UPDATED_DRAFT));
        }
      } catch (e) {
        sentryError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [addSponsoredAdvert, currentlyEditedAd, popSuccess, selectedAd, t],
  );

  const uploadSponsoredListingImage: DropzoneUpload = useCallback(
    async (file, fileName, config, customName) => {
      try {
        const fileUploaded =
          await api.sponsoredListings.uploadSponsoredAdvertImage(
            file,
            fileName,
            currentlyEditedAd._id,
            config,
            customName,
          );

        if (!getIsAdStatusDrafted(currentlyEditedAd.status)) {
          addSponsoredAdvert(selectedAd ? 'update' : 'add', {
            ...currentlyEditedAd,
            [fileName]: fileUploaded,
          });
        }

        return fileUploaded;
      } catch (e) {
        popServerError(e);
      }
    },
    [addSponsoredAdvert, currentlyEditedAd, popServerError, selectedAd],
  );

  const deleteSponsoredListingImage: DropzoneDelete = useCallback(
    async (file, fileName) => {
      try {
        const {
          data: { fileId },
        } = await api.sponsoredListings.deleteSponsoredAdvertImage(
          currentlyEditedAd._id,
          fileName,
          file.id,
        );

        if (!getIsAdStatusDrafted(currentlyEditedAd.status)) {
          addSponsoredAdvert(selectedAd ? 'update' : 'add', {
            ...currentlyEditedAd,
            [fileName]: null,
          });
        }

        return fileId;
      } catch (e) {
        popServerError(e);
      }
    },
    [currentlyEditedAd, addSponsoredAdvert, selectedAd, popServerError],
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={currentlyEditedAd}
      render={({ values, handleSubmit }) => {
        return (
          <form onSubmit={handleSubmit} className={classes}>
            <div
              className={classNames('advertising-business-card', {
                'apply-responsive': getIsAdTypeMedRec(values.cardType),
              })}
            >
              <div className="advertising-business-card__title-section">
                <StatusBadge sponsored />
                <DetailPageField
                  name="title"
                  previewType="p"
                  placeholder={t('listingTitle')}
                  field={{ component: InputField, maxLength: 90 }}
                />
              </div>
              <div className="advertising-business-card__image-container">
                <DetailPageField
                  name="coverPhoto"
                  previewType="images"
                  field={{
                    fileName: COVER_PHOTO_FILE_NAME,
                    component: DropzoneField,
                    uploadRequest: uploadSponsoredListingImage,
                    deleteRequest: deleteSponsoredListingImage,
                    accept: 'image/*',
                    maxFiles: 1,
                  }}
                />
              </div>
              <DetailPageField
                name="description"
                className="advertising-business-card__description"
                previewType="p"
                placeholder={t('description')}
                field={{ component: TextAreaField }}
              />

              <div className="advertising-business-card__cta-container">
                <DetailPageField
                  name="ctaText"
                  previewType="advertCta"
                  placeholder="Link"
                  renderEditor={() => (
                    <div className="cta-editor">
                      <Field
                        name="ctaText"
                        component={InputField}
                        label={t('linkTitle')}
                        maxLength={20}
                      />
                      <Field
                        name="link"
                        className="url-field"
                        component={InputField}
                        label={t('urlLink')}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="advertising-business-card__logo-container">
                <DetailPageField
                  className="logo"
                  name="logoImage"
                  previewType="images"
                  field={{
                    fileName: LOGO_FILE_NAME,
                    component: DropzoneField,
                    uploadRequest: uploadSponsoredListingImage,
                    deleteRequest: deleteSponsoredListingImage,
                    accept: 'image/*',
                    maxFiles: 1,
                  }}
                />
              </div>
            </div>
            <div className="edit-selected-ad__actions">
              <Field
                name="cardType"
                slideSteps={cardOptions}
                component={RadioSliderField}
              />
              {selectedAd && (
                <div className="edit-selected-ad__actions__buttons">
                  <Button
                    variant="outline"
                    icon={isPaused ? PlayIcon : PauseIcon}
                    onClick={togglePauseAdvert}
                  >
                    {t(isPaused ? 'unpause' : 'pause')}
                  </Button>

                  <Button
                    variant="link"
                    icon={TrashIcon}
                    className="warning-action-button"
                    onClick={deleteAdvert}
                  >
                    {t('delete')}
                  </Button>
                </div>
              )}
              {isLoading && <Loader overlay={false} />}
            </div>
            <div className="edit-selected-ad__submit-container">
              <Button type="submit">
                {t(selectedAd ? 'saveAdChanges' : 'createNewAdvert')}
              </Button>
            </div>

            {isLoading && <Loader />}
          </form>
        );
      }}
    />
  );
};

export default EditSelectedAd;
